import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  TextField,
  Alert
} from '@mui/material';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';

const MyListings = () => {
  const navigate = useNavigate();
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gymToDelete, setGymToDelete] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewGym, setPreviewGym] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentListing, setCurrentListing] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    pricePerHour: '',
    maxPeople: '',
    personalTrainingPremium: '',
    amenities: {
      parking: false,
      showers: false,
      wifi: false,
      personalTraining: false,
      refreshments: false,
      towelService: false,
      saunaSteamRoom: false
    },
    availableHours: {
      monday: { start: '09:00', end: '21:00', enabled: true },
      tuesday: { start: '09:00', end: '21:00', enabled: true },
      wednesday: { start: '09:00', end: '21:00', enabled: true },
      thursday: { start: '09:00', end: '21:00', enabled: true },
      friday: { start: '09:00', end: '21:00', enabled: true },
      saturday: { start: '09:00', end: '21:00', enabled: true },
      sunday: { start: '09:00', end: '21:00', enabled: true }
    }
  });

  const fetchMyGyms = async () => {
    try {
      setLoading(true);
      console.log('Fetching my listings...');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to view your listings');
        setLoading(false);
        return;
      }

      const response = await axios.get('/api/gyms/my-listings');
      
      console.log('Received listings:', response.data);
      if (!Array.isArray(response.data)) {
        console.error('Invalid response format:', response.data);
        setError('Invalid response from server');
        return;
      }
      
      setGyms(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching listings:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });
      
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You are not authorized to view these listings.');
      } else {
        const errorMessage = err.response?.data?.error || 
                           err.response?.data?.details || 
                           err.message || 
                           'Failed to fetch your listings';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyGyms();
  }, []);

  const handleViewGym = (gym) => {
    if (gym.status === 'draft') {
      setPreviewGym(gym);
      setPreviewDialogOpen(true);
    } else {
      navigate(`/gyms/${gym._id}`);
    }
  };

  const handleDeleteClick = (gym) => {
    setGymToDelete(gym);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      console.log('Deleting gym with ID:', gymToDelete._id);
      await axios.delete(`/api/gyms/${gymToDelete._id}`);
      setGyms(prevGyms => prevGyms.filter(gym => gym._id !== gymToDelete._id));
      setDeleteDialogOpen(false);
      setGymToDelete(null);
      setError(null); // Clear any previous errors
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete gym';
      setError(errorMessage);
      console.error('Error deleting gym:', {
        error: errorMessage,
        response: err.response?.data,
        gymId: gymToDelete._id
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGymToDelete(null);
  };

  const handleStatusChange = async (gymId, newStatus) => {
    try {
      console.log('Updating gym status:', { gymId, newStatus });
      const response = await axios.patch(`/api/gyms/${gymId}/status`, { status: newStatus });
      setGyms(prevGyms => 
        prevGyms.map(gym => 
          gym._id === gymId ? { ...gym, status: newStatus } : gym
        )
      );
      setError(null); // Clear any previous errors
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update gym status';
      setError(errorMessage);
      console.error('Error updating gym status:', {
        error: errorMessage,
        response: err.response?.data,
        gymId,
        newStatus
      });
    }
  };

  const handleEdit = (listing) => {
    setCurrentListing(listing);
    setEditFormData({
      name: listing.name,
      description: listing.description,
      pricePerHour: listing.pricePerHour,
      maxPeople: listing.maxPeople,
      personalTrainingPremium: listing.personalTrainingPremium || '',
      amenities: {
        parking: listing.amenities?.parking || false,
        showers: listing.amenities?.showers || false,
        wifi: listing.amenities?.wifi || false,
        personalTraining: listing.amenities?.personalTraining || false,
        refreshments: listing.amenities?.refreshments || false,
        towelService: listing.amenities?.towelService || false,
        saunaSteamRoom: listing.amenities?.saunaSteamRoom || false
      },
      availableHours: listing.availableHours || {
        monday: { start: '09:00', end: '21:00', enabled: true },
        tuesday: { start: '09:00', end: '21:00', enabled: true },
        wednesday: { start: '09:00', end: '21:00', enabled: true },
        thursday: { start: '09:00', end: '21:00', enabled: true },
        friday: { start: '09:00', end: '21:00', enabled: true },
        saturday: { start: '09:00', end: '21:00', enabled: true },
        sunday: { start: '09:00', end: '21:00', enabled: true }
      }
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/gyms/${currentListing._id}`, editFormData);
      setGyms(gyms.map(gym => 
        gym._id === currentListing._id 
          ? { ...gym, ...editFormData }
          : gym
      ));
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating listing:', err);
      setError('Failed to update listing');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('amenities.')) {
      const amenity = name.split('.')[1];
      setEditFormData(prev => ({
        ...prev,
        amenities: {
          ...prev.amenities,
          [amenity]: checked
        }
      }));
    } else if (name.startsWith('availableHours.')) {
      const [_, day, field] = name.split('.');
      setEditFormData(prev => ({
        ...prev,
        availableHours: {
          ...prev.availableHours,
          [day]: {
            ...prev.availableHours[day],
            [field]: field === 'enabled' ? checked : value
          }
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          My Listings
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/list-gym')}
        >
          List New Gym
        </Button>
      </Box>

      {gyms.length === 0 ? (
        <Typography variant="h6" color="text.secondary" align="center">
          You haven't listed any gyms yet.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {gyms.map((gym) => (
            <Grid item xs={12} sm={6} md={4} key={gym._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}
              >
                {gym.status === 'draft' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'warning.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      zIndex: 1
                    }}
                  >
                    Draft
                  </Box>
                )}
                <CardMedia
                  component="img"
                  height="200"
                  image={gym.photos[0] || 'https://via.placeholder.com/400x200?text=No+Image+Available'}
                  alt={gym.name}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {gym.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {gym.description}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary">
                      ${gym.pricePerHour}/hr
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Max {gym.maxPeople} people
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      startIcon={<Visibility />}
                      onClick={() => handleViewGym(gym)}
                    >
                      {gym.status === 'draft' ? 'Preview' : 'View'}
                    </Button>
                    <Button
                      startIcon={<Edit />}
                      onClick={() => handleEdit(gym)}
                    >
                      Edit
                    </Button>
                    <Button
                      color={gym.status === 'listed' ? 'warning' : 'success'}
                      onClick={() => handleStatusChange(gym._id, gym.status === 'listed' ? 'draft' : 'listed')}
                    >
                      {gym.status === 'listed' ? 'Unpublish' : 'Publish'}
                    </Button>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(gym)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview: {previewGym?.name}
          <Typography variant="caption" color="warning.main" sx={{ ml: 2 }}>
            (Draft)
          </Typography>
        </DialogTitle>
        <DialogContent>
          {previewGym && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ position: 'relative', width: '100%', height: 300, mb: 2 }}>
                <img
                  src={previewGym.photos[0] || 'https://via.placeholder.com/800x400?text=No+Image+Available'}
                  alt={previewGym.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 4
                  }}
                />
              </Box>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography paragraph>
                {previewGym.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" color="primary">
                    Price: ${previewGym.pricePerHour}/hr
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">
                    Max Capacity: {previewGym.maxPeople} people
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Amenities:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {previewGym.amenities && Object.entries(previewGym.amenities).map(([key, value]) => {
                      const boolValue = typeof value === 'string' ? value === 'true' : Boolean(value);
                      return boolValue && (
                        <Chip 
                          key={key}
                          label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')} 
                          color="primary" 
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              handleStatusChange(previewGym._id, 'listed');
              setPreviewDialogOpen(false);
            }}
          >
            Publish Now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Gym Listing</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{gymToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Listing</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Price per Hour"
                  name="pricePerHour"
                  type="number"
                  value={editFormData.pricePerHour}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max People"
                  name="maxPeople"
                  type="number"
                  value={editFormData.maxPeople}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Personal Training Premium (per hour)"
                  name="personalTrainingPremium"
                  type="number"
                  value={editFormData.personalTrainingPremium}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  helperText="Set the premium price for personal training (optional)"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Amenities</Typography>
                <Grid container spacing={1}>
                  {Object.keys(editFormData.amenities).map((key) => (
                    <Grid item xs={6} sm={4} md={3} key={key}>
                      <label>
                        <input
                          type="checkbox"
                          name={`amenities.${key}`}
                          checked={editFormData.amenities[key]}
                          onChange={handleInputChange}
                          style={{ marginRight: 8 }}
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Available Hours</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set the days and hours when your gym is available for booking
                </Typography>
                <Grid container spacing={2}>
                  {editFormData.availableHours && Object.entries(editFormData.availableHours).map(([day, schedule]) => (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Box sx={{ p: 2, border: schedule.enabled ? '2px solid #1976d2' : '2px solid #e0e0e0', borderRadius: 1 }}>
                        <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                          <input
                            type="checkbox"
                            name={`availableHours.${day}.enabled`}
                            checked={schedule.enabled}
                            onChange={handleInputChange}
                            style={{ marginRight: 8 }}
                          />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                            {day}
                          </Typography>
                        </label>
                        {schedule.enabled && (
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                label="Open"
                                type="time"
                                name={`availableHours.${day}.start`}
                                value={schedule.start}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                label="Close"
                                type="time"
                                name={`availableHours.${day}.end`}
                                value={schedule.end}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        )}
                        {!schedule.enabled && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Closed
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyListings; 